import type { LocationMethod } from "./types";

export const STAGE2_SCHEMA_VERSION = 2 as const;

export const CONDITION_SCALE_DIRECTION = "5_IS_BEST" as const satisfies
  | "5_IS_BEST"
  | "1_IS_BEST";

export const CONDITION_SCALE_VERSION = "semantic-v1";

export const CONDITION_OPTIONS = [
  { code: "very_good", label: "Very good" },
  { code: "good", label: "Good" },
  { code: "fair", label: "Fair" },
  { code: "poor", label: "Poor" },
  { code: "critical_failed", label: "Critical / failed" },
  { code: "not_inspected_unknown", label: "Not inspected / unknown" },
] as const;

export type ConditionCode = (typeof CONDITION_OPTIONS)[number]["code"];

export const LOCATION_METHOD_LABELS: Record<LocationMethod, string> = {
  place_search: "Gazetteer / place search",
  map_pin: "Map pin",
  device_location: "Device location",
  coordinate_entry: "Coordinate entry",
  official_import: "Official list / import",
  manual_trace: "Manual line / polygon trace",
  paste_app: "Pasted from a map app",
};

export const SENSITIVITY_OPTIONS = [
  { code: "public", label: "Public" },
  { code: "project-restricted", label: "Project-restricted" },
  { code: "sensitive", label: "Sensitive" },
] as const;

export const FEATURE_FLAGS = {
  manualGeometry: true,
} as const;

export function legacyLocationMethod(value: string): LocationMethod {
  const normalised = value.trim().toLowerCase();
  if (normalised === "google_search" || normalised.includes("google place")) {
    return "place_search";
  }
  if (normalised.includes("gazetteer") || normalised.includes("place search")) {
    return "place_search";
  }
  if (normalised.includes("paste")) return "paste_app";
  if (normalised.includes("device") || normalised.includes("gps")) {
    return "device_location";
  }
  if (normalised.includes("official") || normalised.includes("coordinate")) {
    return normalised.includes("digit") ? "manual_trace" : "coordinate_entry";
  }
  if (normalised.includes("digit") || normalised.includes("trace")) {
    return "manual_trace";
  }
  return "map_pin";
}

export function conditionNumber(
  code: ConditionCode,
  direction: "5_IS_BEST" | "1_IS_BEST" = CONDITION_SCALE_DIRECTION,
) {
  if (code === "not_inspected_unknown") return null;
  const bestToWorst: ConditionCode[] = [
    "very_good",
    "good",
    "fair",
    "poor",
    "critical_failed",
  ];
  const index = bestToWorst.indexOf(code);
  return direction === "5_IS_BEST" ? 5 - index : index + 1;
}

export function migrateLegacyCondition(
  value: string,
  direction?: "5_IS_BEST" | "1_IS_BEST",
): { code: ConditionCode; reviewRequired: boolean } {
  if (CONDITION_OPTIONS.some((item) => item.code === value)) {
    return { code: value as ConditionCode, reviewRequired: false };
  }
  if (value === "not-assessed" || !value) {
    return { code: "not_inspected_unknown", reviewRequired: false };
  }
  const number = Number(value);
  if (!Number.isInteger(number) || number < 1 || number > 5 || !direction) {
    return { code: "not_inspected_unknown", reviewRequired: Boolean(value) };
  }
  const bestIndex = direction === "5_IS_BEST" ? 5 - number : number - 1;
  return {
    code: ["very_good", "good", "fair", "poor", "critical_failed"][
      bestIndex
    ] as ConditionCode,
    reviewRequired: false,
  };
}
